import Swal from "sweetalert2";
import "sweetalert2/dist/sweetalert2.min.css";
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Button,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Spinner,
} from "reactstrap";

import {
  formatCentsToMoneyInput,
  formatMoneyFromCents,
  parseMoneyInputToCents,
} from "../../../../helpers/money";
import {
  applyDiscount,
  ApplyOrderDiscountPayload,
  getCourtesyReasonSuggestions,
  previewDiscount,
} from "../../../../services/orderService";

export interface ApplyDiscountInitialValues {
  percentBps: number | null;
  amountCents: number;
  reason: string | null;
}

interface ApplyDiscountModalProps {
  isOpen: boolean;
  toggle: () => void;
  orderId: number;
  subtotalCents: number;
  maxManualDiscountBps: number;
  showSettingsLink?: boolean;
  initialDiscount?: ApplyDiscountInitialValues | null;
  onApplied: () => void;
}

type DiscountMode = "percent" | "amount";

type BuildResult =
  | { ok: true; payload: ApplyOrderDiscountPayload }
  | { ok: false; error: string };

const ApplyDiscountModal = ({
  isOpen,
  toggle,
  orderId,
  subtotalCents,
  maxManualDiscountBps,
  showSettingsLink = false,
  initialDiscount = null,
  onApplied,
}: ApplyDiscountModalProps) => {
  const { t, i18n } = useTranslation();
  const [mode, setMode] = useState<DiscountMode>("percent");
  const [percentInput, setPercentInput] = useState("10");
  const [amountInput, setAmountInput] = useState("");
  const [reason, setReason] = useState("");
  const [previewTotal, setPreviewTotal] = useState<number | null>(null);
  const [previewDiscountCents, setPreviewDiscountCents] = useState<number | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [recentReasons, setRecentReasons] = useState<string[]>([]);

  const maxPercent = useMemo(
    () => Math.max(0, Math.round(maxManualDiscountBps) / 100),
    [maxManualDiscountBps]
  );

  const maxAmountCents = useMemo(
    () => Math.floor((subtotalCents * Math.max(0, maxManualDiscountBps)) / 10000),
    [subtotalCents, maxManualDiscountBps]
  );

  const halfMaxAmountCents = useMemo(
    () => Math.floor(maxAmountCents / 2),
    [maxAmountCents]
  );

  const defaultReasonSuggestions = useMemo(
    () => [
      t("studio.orders.discount.reasonChip.frequent"),
      t("studio.orders.discount.reasonChip.priceError"),
      t("studio.orders.discount.reasonChip.campaign"),
    ],
    [t]
  );

  const reasonSuggestions = useMemo(() => {
    const merged: string[] = [];
    const seen = new Set<string>();

    [...recentReasons, ...defaultReasonSuggestions].forEach((item) => {
      const key = item.trim().toLowerCase();
      if (!key || seen.has(key)) {
        return;
      }
      seen.add(key);
      merged.push(item.trim());
    });

    return merged.slice(0, 8);
  }, [defaultReasonSuggestions, recentReasons]);

  const percentChips = useMemo(() => {
    const candidates = [10, 15, maxPercent].filter(
      (percent) => percent > 0 && percent <= maxPercent
    );

    return Array.from(new Set(candidates)).sort((a, b) => a - b);
  }, [maxPercent]);

  const formatMoney = useMemo(
    () => (cents: number) => formatMoneyFromCents(cents, i18n.language),
    [i18n.language]
  );

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    let cancelled = false;

    void getCourtesyReasonSuggestions()
      .then((response) => {
        if (!cancelled) {
          setRecentReasons(response.data?.reasons ?? []);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setRecentReasons([]);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    if (initialDiscount && initialDiscount.amountCents > 0) {
      if (initialDiscount.percentBps != null && initialDiscount.percentBps > 0) {
        setMode("percent");
        setPercentInput(String(initialDiscount.percentBps / 100));
        setAmountInput(formatCentsToMoneyInput(0));
      } else {
        setMode("amount");
        setPercentInput(maxPercent > 0 ? String(Math.min(10, maxPercent)) : "1");
        setAmountInput(formatCentsToMoneyInput(initialDiscount.amountCents));
      }
      setReason(initialDiscount.reason ?? "");
    } else {
      setMode("percent");
      setPercentInput(maxPercent > 0 ? String(Math.min(10, maxPercent)) : "1");
      setAmountInput(formatCentsToMoneyInput(0));
      setReason("");
    }

    setPreviewTotal(null);
    setPreviewDiscountCents(null);
    setErrorMessage(null);
    setSubmitting(false);
    // Seed only when the modal opens — avoid resetting while typing.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional open-edge init
  }, [isOpen]);

  const clearPreview = () => {
    setPreviewTotal(null);
    setPreviewDiscountCents(null);
  };

  const applyPercentChip = (percent: number) => {
    setMode("percent");
    setPercentInput(String(percent));
    clearPreview();
    setErrorMessage(null);
  };

  const buildPayload = (): BuildResult => {
    const trimmedReason = reason.trim() || null;

    if (mode === "percent") {
      const percent = Number(percentInput.replace(",", "."));
      if (!Number.isFinite(percent) || percent <= 0) {
        return { ok: false, error: t("studio.orders.discount.validation.required") };
      }

      if (percent > maxPercent) {
        return {
          ok: false,
          error: t("studio.orders.discount.validation.overCap", { percent: maxPercent }),
        };
      }

      return {
        ok: true,
        payload: {
          discount_percent_bps: Math.round(percent * 100),
          reason: trimmedReason,
        },
      };
    }

    const cents = parseMoneyInputToCents(amountInput);
    if (cents == null || cents <= 0) {
      return { ok: false, error: t("studio.orders.discount.validation.required") };
    }

    if (cents > maxAmountCents) {
      return {
        ok: false,
        error: t("studio.orders.discount.validation.overCap", { percent: maxPercent }),
      };
    }

    return {
      ok: true,
      payload: { discount_amount_cents: cents, reason: trimmedReason },
    };
  };

  const handlePreview = async () => {
    const result = buildPayload();
    if (!result.ok) {
      setErrorMessage(result.error);
      return;
    }

    setSubmitting(true);
    setErrorMessage(null);

    try {
      const response = await previewDiscount(orderId, result.payload);
      setPreviewDiscountCents(response.data.discount_total_cents);
      setPreviewTotal(response.data.total_amount);
    } catch (error: any) {
      setErrorMessage(error?.data || t("studio.orders.discount.toast.previewError"));
    } finally {
      setSubmitting(false);
    }
  };

  const handleApply = async () => {
    const result = buildPayload();
    if (!result.ok) {
      setErrorMessage(result.error);
      return;
    }

    const isAtCap =
      (mode === "percent" &&
        Number(percentInput.replace(",", ".")) >= maxPercent) ||
      (mode === "amount" &&
        parseMoneyInputToCents(amountInput) === maxAmountCents);

    if (isAtCap) {
      const confirmation = await Swal.fire({
        title: t("studio.orders.discount.confirmCapTitle"),
        text: t("studio.orders.discount.confirmCapBody", { percent: maxPercent }),
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: t("studio.orders.discount.apply"),
        cancelButtonText: t("studio.orders.actions.cancel"),
      });

      if (!confirmation.isConfirmed) {
        return;
      }
    }

    setSubmitting(true);
    setErrorMessage(null);

    try {
      await applyDiscount(orderId, result.payload);
      onApplied();
      toggle();
    } catch (error: any) {
      setErrorMessage(error?.data || t("studio.orders.discount.toast.applyError"));
    } finally {
      setSubmitting(false);
    }
  };

  const handleModalKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (submitting) {
      return;
    }

    const target = event.target as HTMLElement;
    const isTextarea = target.tagName === "TEXTAREA";

    if (event.key === "Enter" && event.ctrlKey) {
      event.preventDefault();
      void handleApply();
      return;
    }

    if (event.key === "Enter" && !event.ctrlKey && !isTextarea) {
      event.preventDefault();
      void handlePreview();
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered>
      <div onKeyDown={handleModalKeyDown}>
      <ModalHeader toggle={toggle}>{t("studio.orders.discount.title")}</ModalHeader>
      <ModalBody>
        <p className="text-muted small mb-1">
          {t("studio.orders.discount.help", { subtotal: formatMoney(subtotalCents) })}
        </p>
        <p className="text-muted small">
          {t("studio.orders.discount.capHint", { percent: maxPercent })}
          {showSettingsLink ? (
            <>
              {" "}
              <Link to="/settings/operation" className="link-primary">
                {t("studio.orders.discount.settingsLink")}
              </Link>
            </>
          ) : null}
        </p>

        <p className="text-muted small mb-3">
          {t("studio.orders.discount.keyboardHint")}
        </p>

        <FormGroup>
          <Label className="form-label">{t("studio.orders.discount.modeLabel")}</Label>
          <Input
            type="select"
            value={mode}
            onChange={(event) => {
              setMode(event.target.value as DiscountMode);
              clearPreview();
              setErrorMessage(null);
            }}
          >
            <option value="percent">{t("studio.orders.discount.modePercent")}</option>
            <option value="amount">{t("studio.orders.discount.modeAmount")}</option>
          </Input>
        </FormGroup>

        {mode === "percent" ? (
          <FormGroup>
            <Label className="form-label">{t("studio.orders.discount.percentLabel")}</Label>
            {percentChips.length > 0 ? (
              <div className="d-flex flex-wrap gap-2 mb-2">
                {percentChips.map((percent) => (
                  <Button
                    key={percent}
                    type="button"
                    color="light"
                    size="sm"
                    outline={Number(percentInput.replace(",", ".")) !== percent}
                    active={Number(percentInput.replace(",", ".")) === percent}
                    disabled={submitting}
                    onClick={() => applyPercentChip(percent)}
                  >
                    {t("studio.orders.discount.percentChip", { percent })}
                  </Button>
                ))}
              </div>
            ) : null}
            <Input
              type="number"
              min={0.01}
              max={maxPercent}
              step={0.01}
              value={percentInput}
              onChange={(event) => {
                setPercentInput(event.target.value);
                clearPreview();
                setErrorMessage(null);
              }}
            />
          </FormGroup>
        ) : (
          <FormGroup>
            <Label className="form-label">{t("studio.orders.discount.amountLabel")}</Label>
            {maxAmountCents > 0 ? (
              <div className="d-flex flex-wrap gap-2 mb-2">
                {halfMaxAmountCents > 0 ? (
                  <Button
                    type="button"
                    color="light"
                    size="sm"
                    outline={
                      parseMoneyInputToCents(amountInput) !== halfMaxAmountCents
                    }
                    active={
                      parseMoneyInputToCents(amountInput) === halfMaxAmountCents
                    }
                    disabled={submitting}
                    onClick={() => {
                      setAmountInput(formatCentsToMoneyInput(halfMaxAmountCents));
                      clearPreview();
                      setErrorMessage(null);
                    }}
                  >
                    {t("studio.orders.discount.halfAmountChip", {
                      amount: formatMoney(halfMaxAmountCents),
                    })}
                  </Button>
                ) : null}
                <Button
                  type="button"
                  color="light"
                  size="sm"
                  outline={
                    parseMoneyInputToCents(amountInput) !== maxAmountCents
                  }
                  active={parseMoneyInputToCents(amountInput) === maxAmountCents}
                  disabled={submitting}
                  onClick={() => {
                    setAmountInput(formatCentsToMoneyInput(maxAmountCents));
                    clearPreview();
                    setErrorMessage(null);
                  }}
                >
                  {t("studio.orders.discount.maxAmountChip", {
                    amount: formatMoney(maxAmountCents),
                  })}
                </Button>
              </div>
            ) : null}
            <Input
              type="text"
              value={amountInput}
              onChange={(event) => {
                setAmountInput(event.target.value);
                clearPreview();
                setErrorMessage(null);
              }}
            />
            <p className="text-muted small mt-1 mb-0">
              {t("studio.orders.discount.maxAmountHint", {
                amount: formatMoney(maxAmountCents),
              })}
            </p>
          </FormGroup>
        )}

        <FormGroup>
          <Label className="form-label">{t("studio.orders.discount.reasonLabel")}</Label>
          <div className="d-flex flex-wrap gap-2 mb-2">
            {reasonSuggestions.map((suggestion) => (
              <Button
                key={suggestion}
                type="button"
                color="light"
                size="sm"
                outline={reason !== suggestion}
                active={reason === suggestion}
                disabled={submitting}
                onClick={() => setReason(suggestion)}
              >
                {suggestion}
              </Button>
            ))}
          </div>
          <Input
            type="textarea"
            rows={2}
            maxLength={500}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            placeholder={t("studio.orders.discount.reasonPlaceholder")}
          />
        </FormGroup>

        {previewTotal != null && previewDiscountCents != null ? (
          <Alert color="info" className="mb-0">
            {t("studio.orders.discount.previewResult", {
              discount: formatMoney(previewDiscountCents),
              total: formatMoney(previewTotal),
            })}
          </Alert>
        ) : null}

        {errorMessage ? (
          <Alert color="danger" className="mt-3 mb-0">
            {errorMessage}
          </Alert>
        ) : null}
      </ModalBody>
      <ModalFooter>
        <Button color="light" onClick={toggle} disabled={submitting}>
          {t("studio.orders.actions.cancel")}
        </Button>
        <Button color="secondary" outline onClick={() => void handlePreview()} disabled={submitting}>
          {submitting ? <Spinner size="sm" /> : t("studio.orders.discount.preview")}
        </Button>
        <Button color="primary" onClick={() => void handleApply()} disabled={submitting}>
          {submitting ? <Spinner size="sm" /> : t("studio.orders.discount.apply")}
        </Button>
      </ModalFooter>
      </div>
    </Modal>
  );
};

export default ApplyDiscountModal;
